import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTickets } from '../hooks/useTickets';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  CircularProgress,
  Paper,
  FormControlLabel,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ColorLens as ColorIcon
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import * as statusService from '../services/statusService';
import { projectsApi } from '../services/api';
import { t } from '../localization';

const StatusManager = ({ projectId }) => {
  const queryClient = useQueryClient();
  const { refetch: refetchTickets } = useTickets();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusName, setStatusName] = useState('');
  const [statusColor, setStatusColor] = useState('#A0AEC0');
  const [isFinal, setIsFinal] = useState(false);
  const [isInitial, setIsInitial] = useState(false);
  const [countsInOverdue, setCountsInOverdue] = useState(false);
  const [error, setError] = useState('');
  const [projectPrefs, setProjectPrefs] = useState({ reworkStatusId: null, authorCloseStatusId: null, authorActionsFromStatusId: null });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Загрузка статусов проекта
  const fetchStatuses = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const data = await statusService.getProjectStatuses(projectId);
      setStatuses(data);
      // подгружаем проект для чтения текущих настроек
      const projRes = await projectsApi.getById(projectId);
      setProjectPrefs({
        reworkStatusId: projRes.data?.reworkStatusId || null,
        authorCloseStatusId: projRes.data?.authorCloseStatusId || null,
        authorActionsFromStatusId: projRes.data?.authorActionsFromStatusId || null
      });
    } catch (error) {
      console.error('Ошибка при загрузке статусов:', error);
      setError('Не удалось загрузить статусы проекта');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, [projectId]);

  // Открытие диалога создания/редактирования статуса
  const handleOpenDialog = (status = null) => {
    setCurrentStatus(status);
    setStatusName(status ? status.name : '');
    setStatusColor(status ? status.color : '#A0AEC0');
    setIsFinal(status ? status.isFinal : false);
    setIsInitial(status ? status.isInitial : false);
    setCountsInOverdue(status ? status.countsInOverdue === true : false);
    setDialogOpen(true);
  };

  const handleSavePreferences = async () => {
    try {
      setSavingPrefs(true);
      await projectsApi.update(projectId, {
        reworkStatusId: projectPrefs.reworkStatusId || null,
        authorCloseStatusId: projectPrefs.authorCloseStatusId || null,
        authorActionsFromStatusId: projectPrefs.authorActionsFromStatusId || null
      });
      // Обновим локально проектные настройки
      const projRes = await projectsApi.getById(projectId);
      setProjectPrefs({
        reworkStatusId: projRes.data?.reworkStatusId || null,
        authorCloseStatusId: projRes.data?.authorCloseStatusId || null,
        authorActionsFromStatusId: projRes.data?.authorActionsFromStatusId || null
      });
      setSnackbarOpen(true);
    } catch (e) {
      console.error('Ошибка сохранения настроек статусов проекта:', e);
      alert(e.response?.data?.error || 'Ошибка сохранения настроек');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleSnackbarClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Закрытие диалога
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentStatus(null);
    setStatusName('');
    setStatusColor('#A0AEC0');
    setIsFinal(false);
    setIsInitial(false);
    setCountsInOverdue(false);
    setError('');
  };

  // Сохранение статуса
  const handleSaveStatus = async () => {
    if (!statusName.trim()) {
      setError('Название статуса не может быть пустым');
      return;
    }

    try {
      if (currentStatus) {
        // Обновление существующего статуса
        await statusService.updateStatus(currentStatus.id, {
          name: statusName,
          color: statusColor,
          isFinal: isFinal,
          isInitial: isInitial,
          countsInOverdue: countsInOverdue
        });
      } else {
        // Создание нового статуса
        await statusService.createStatus(projectId, {
          name: statusName,
          color: statusColor,
          isFinal: isFinal,
          isInitial: isInitial,
          countsInOverdue: countsInOverdue
        });
      }
      
      await fetchStatuses();
      // Инвалидируем кэш тикетов, чтобы обновить цвета статусов
      queryClient.invalidateQueries(['tickets']);
      // Прямой вызов refetch для немедленного обновления данных
      refetchTickets();
      handleCloseDialog();
    } catch (error) {
      console.error('Ошибка при сохранении статуса:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Произошла ошибка при сохранении статуса');
      }
    }
  };

  // Удаление статуса
  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот статус? Если к нему привязаны тикеты, удаление будет невозможно.')) {
      return;
    }

    try {
      await statusService.deleteStatus(statusId);
      await fetchStatuses();
      // Инвалидируем кэш тикетов, чтобы обновить статусы
      queryClient.invalidateQueries(['tickets']);
      // Прямой вызов refetch для немедленного обновления данных
      refetchTickets();
    } catch (error) {
      console.error('Ошибка при удалении статуса:', error);
      alert(error.response?.data?.error || 'Произошла ошибка при удалении статуса');
    }
  };

  // Перемещение статуса вверх по списку
  const handleMoveUp = async (index) => {
    if (index === 0) return; // Уже в начале списка
    
    const items = Array.from(statuses);
    const temp = items[index];
    items[index] = items[index - 1];
    items[index - 1] = temp;
    
    // Оптимистичное обновление UI
    setStatuses(items);
    
    // Отправка нового порядка на сервер
    try {
      await statusService.reorderStatuses(projectId, items.map(item => item.id));
      // Инвалидируем кэш тикетов, чтобы обновить порядок статусов
      queryClient.invalidateQueries(['tickets']);
    } catch (error) {
      console.error('Ошибка при изменении порядка статусов:', error);
      // В случае ошибки перезагружаем статусы с сервера
      fetchStatuses();
    }
  };

  // Перемещение статуса вниз по списку
  const handleMoveDown = async (index) => {
    if (index === statuses.length - 1) return; // Уже в конце списка
    
    const items = Array.from(statuses);
    const temp = items[index];
    items[index] = items[index + 1];
    items[index + 1] = temp;
    
    // Оптимистичное обновление UI
    setStatuses(items);
    
    // Отправка нового порядка на сервер
    try {
      await statusService.reorderStatuses(projectId, items.map(item => item.id));
      // Инвалидируем кэш тикетов, чтобы обновить порядок статусов
      queryClient.invalidateQueries(['tickets']);
    } catch (error) {
      console.error('Ошибка при изменении порядка статусов:', error);
      // В случае ошибки перезагружаем статусы с сервера
      fetchStatuses();
    }
  };

  if (!projectId) {
    return <Typography>Выберите проект для управления статусами</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Управление статусами</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить статус
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : statuses.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Для этого проекта еще не создано ни одного статуса</Typography>
        </Paper>
      ) : (
        <Paper sx={{ mb: 3 }}>
          <List>
            {statuses.map((status, index) => (
              <ListItem
                key={status.id}
                divider
                secondaryAction={
                  <Box>
                    <Tooltip title="Переместить вверх">
                      <IconButton 
                        edge="end" 
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUpIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Переместить вниз">
                      <IconButton 
                        edge="end" 
                        onClick={() => handleMoveDown(index)}
                        disabled={index === statuses.length - 1}
                      >
                        <ArrowDownIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Редактировать">
                      <IconButton edge="end" onClick={() => handleOpenDialog(status)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <IconButton edge="end" color="error" onClick={() => handleDeleteStatus(status.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <Box 
                  sx={{ 
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%', 
                    backgroundColor: status.color,
                    mr: 2
                  }} 
                />
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" component="span">
                        {status.name}
                      </Typography>
                      {status.isFinal && 
                        <Chip 
                          size="small" 
                          color="warning" 
                          label="Финальный" 
                          sx={{ fontSize: '0.7rem' }} 
                        />
                      }
                      {status.isInitial && 
                        <Chip 
                          size="small" 
                          color="info" 
                          label="Начальный" 
                          sx={{ fontSize: '0.7rem' }} 
                        />
                      }
                      {status.countsInOverdue && (
                        <Chip 
                          size="small"
                          color="success"
                          label="В просрочке"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Блок настроек статусов проекта */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Настройки действий статусов</Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Box sx={{ minWidth: 320, maxWidth: 480 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Статус для возврата на доработку</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={projectPrefs.reworkStatusId || ''}
                displayEmpty
                onChange={(e) => setProjectPrefs(p => ({ ...p, reworkStatusId: e.target.value || null }))}
                renderValue={(val) => {
                  if (val === '' || val === null || val === undefined) return <span style={{ opacity: 0.6 }}>Не задан</span>;
                  const st = statuses.find(s => String(s.id) === String(val));
                  return st ? st.name : <span style={{ opacity: 0.6 }}>Не задан</span>;
                }}
              >
                <MenuItem value=""><em>Не задан</em></MenuItem>
                {statuses.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: 320, maxWidth: 480 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Статус для закрытия автором</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={projectPrefs.authorCloseStatusId || ''}
                displayEmpty
                onChange={(e) => setProjectPrefs(p => ({ ...p, authorCloseStatusId: e.target.value || null }))}
                renderValue={(val) => {
                  if (val === '' || val === null || val === undefined) return <span style={{ opacity: 0.6 }}>Не задан</span>;
                  const st = statuses.find(s => String(s.id) === String(val));
                  return st ? st.name : <span style={{ opacity: 0.6 }}>Не задан</span>;
                }}
              >
                <MenuItem value=""><em>Не задан</em></MenuItem>
                {statuses.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: 320, maxWidth: 480 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Автор может действовать из статуса</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={projectPrefs.authorActionsFromStatusId || ''}
                displayEmpty
                onChange={(e) => setProjectPrefs(p => ({ ...p, authorActionsFromStatusId: e.target.value || null }))}
                renderValue={(val) => {
                  if (val === '' || val === null || val === undefined) return <span style={{ opacity: 0.6 }}>Любой (кроме финального)</span>;
                  const st = statuses.find(s => String(s.id) === String(val));
                  return st ? st.name : <span style={{ opacity: 0.6 }}>Любой (кроме финального)</span>;
                }}
              >
                <MenuItem value=""><em>Любой (кроме финального)</em></MenuItem>
                {statuses.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="contained" onClick={handleSavePreferences} disabled={savingPrefs}>
              Сохранить настройки
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message="Настройки сохранены"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Диалог создания/редактирования статуса */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentStatus ? 'Редактировать статус' : 'Создать новый статус'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название статуса"
            fullWidth
            value={statusName}
            onChange={(e) => setStatusName(e.target.value)}
            error={!!error}
            helperText={error}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={isFinal}
                onChange={(e) => setIsFinal(e.target.checked)}
                name="isFinal"
              />
            }
            label="Финальный статус (нельзя изменять тикеты в этом статусе, только комментировать)"
            sx={{ mt: 2, mb: 1 }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={isInitial}
                onChange={(e) => setIsInitial(e.target.checked)}
                name="isInitial"
              />
            }
            label="Начальный статус (автоматически присваивается всем новым тикетам)"
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={countsInOverdue}
                onChange={(e) => setCountsInOverdue(e.target.checked)}
                name="countsInOverdue"
              />
            }
            label="Учитывать при расчёте просроченных задач"
            sx={{ mb: 1 }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Цвет статуса
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: '4px', 
                  backgroundColor: statusColor,
                  cursor: 'pointer',
                  border: '1px solid #ccc',
                  mr: 2
                }}
                onClick={() => setColorPickerOpen(!colorPickerOpen)}
              />
              <TextField 
                value={statusColor} 
                onChange={(e) => setStatusColor(e.target.value)}
                size="small"
              />
              <IconButton onClick={() => setColorPickerOpen(!colorPickerOpen)}>
                <ColorIcon />
              </IconButton>
            </Box>
            
            {colorPickerOpen && (
              <Box sx={{ mt: 2, position: 'relative', zIndex: 1000 }}>
                <HexColorPicker color={statusColor} onChange={setStatusColor} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSaveStatus} variant="contained" color="primary">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StatusManager;
